import { ExamLiveCandidateDtoClient } from "@/api/live-exam/dto/exam-live-candidate.dto";
import { Badge } from "@/components/atoms/Badge";
import { Button } from "@/components/atoms/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/atoms/Dialog";
import { Input } from "@/components/atoms/Input";
import { ComboboxField } from "@/components/molecules/Combobox/Combobox";
import { socket } from "@/hooks/socket";
import { formatDate } from "@/lib/utils";
import { DateTime } from "luxon";
import { Clock } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";

const HourDisplay = ({ date, text }: { date?: Date; text: string }) => (
  <div className="flex flex-col gap-1.5">
    <span>{text}</span>
    <Input
      name={text}
      disabled={true}
      value={date ? formatDate(date, "HH:mm") : "-"}
    />
  </div>
);

interface PlannedStartTimeDialogProps {
  registration: ExamLiveCandidateDtoClient;
}

const PlannedStartTimeDialog = ({
  registration: { candidate, examConnection, registration, examDate },
}: PlannedStartTimeDialogProps) => {
  const [minutesFromMidnight, setMinutesFromMidnight] = useState(() => {
    const plannedTime = examConnection?.plannedStartTime;
    if (plannedTime) {
      const parisTime = DateTime.fromJSDate(new Date(plannedTime)).setZone(
        "Europe/Paris",
      );
      return parisTime.hour * 60 + parisTime.minute;
    }
    return 13 * 60; // Default to 13:00
  });
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(false);

  const onAcknowledge = (success: boolean) => {
    setPending(false);
    if (success) {
      setOpen(false);
      toast.success("Horaire de début enregistré.");
    } else
      toast.error("Erreur lors de l'enregistrement de l'horaire de début.");
  };

  const timeOptions = useMemo(
    () =>
      Array.from({ length: 24 * 12 }, (_, i) => {
        const totalMinutes = i * 5;
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;

        return {
          value: totalMinutes,
          label: `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`,
        };
      }),
    [],
  );

  return (
    <Dialog open={open} onOpenChange={(open) => setOpen(open)}>
      <DialogTrigger
        className="flex flex-row gap-2 items-center p-2 hover:bg-gray-50 transition-colors rounded-sm"
        onClick={(e) => e.stopPropagation()}
      >
        <Clock className="w-4 h-4" />
        <span>Modifier l'horaire de début</span>
      </DialogTrigger>
      <DialogContent className="flex flex-col gap-6">
        <DialogHeader>
          <DialogTitle>
            {candidate.lastName} {candidate.firstName}
          </DialogTitle>
          <DialogDescription hidden={true}>
            Modification horaire de début
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          <div className="flex flex-row gap-4">
            <span className="text-black">Périmètre de l'examen</span>
            <Badge variant="white">{registration.perimeter}</Badge>
          </div>
          <HourDisplay
            text="Horaire de début actuel"
            date={examConnection?.plannedStartTime}
          />
          <div className="flex flex-col gap-1.5">
            <label htmlFor="plannedStartTime">Nouvel horaire de début</label>
            <ComboboxField
              options={timeOptions}
              placeholder="-"
              value={minutesFromMidnight}
              onChange={setMinutesFromMidnight}
              id="plannedStartTime"
              displaySearchBox={false}
            />
          </div>
          <Button
            onClick={() => {
              setPending(true);
              const examDateParis = DateTime.fromJSDate(
                new Date(examDate),
              ).setZone("Europe/Paris");
              const hours = Math.floor(minutesFromMidnight / 60);
              const minutes = minutesFromMidnight % 60;
              const plannedStartTime = examDateParis
                .set({
                  hour: hours,
                  minute: minutes,
                  second: 0,
                  millisecond: 0,
                })
                .toJSDate();

              socket.emit(
                "setPlannedStartTime",
                {
                  registrationId: registration.id,
                  plannedStartTime,
                },
                onAcknowledge,
              );
            }}
            disabled={pending}
          >
            {pending ? "En cours..." : "Valider le nouvel horaire de début"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PlannedStartTimeDialog;
